import React, { memo, useState } from 'react';
import { isValidGradingInput, GRADING_INPUT_PATTERN, checkIs3DigitDecimalSigned, trimGradingDecimals } from '../../../../utils/calculationHelpers';

/**
 * Reads a stored rule back verbatim, for the field the user is currently typing in.
 *
 * Formatting rewrites the value, and feeding a rewritten value into a controlled input
 * mid-keystroke throws the typed character away and drops the caret at the end of the field.
 * That is why a rule could not be left at fewer than three decimals: the '.' of '2.5' was
 * swallowed on the very render that followed the keypress, so the digit after it never had a
 * point to attach to (BM-9884). While the field has focus the raw value is echoed instead, so
 * the user sees exactly what they typed.
 * @param {string|number|null|undefined} value stored grade rule
 * @returns {string}
 */
export const rawGradingValue = (value) => (value === undefined || value === null ? '' : String(value));

/**
 * One grade-rule input of one POM row in the Grading Group library.
 *
 * Both size-range layouts of the table render the same cell, so the input lives here once
 * rather than twice, and the value/validation contract cannot drift between them.
 * @param {Object} props
 * @param {string|number|null|undefined} props.value stored grade rule
 * @param {number} props.rowIndex POM row the rule belongs to
 * @param {number} props.sizeIndex size column the rule belongs to
 * @param {boolean} props.disabled true when the user has no edit access
 * @param {Function} props.onChange receives (value, rowIndex, sizeIndex) for an accepted edit
 */
function GradingCell({ value, rowIndex, sizeIndex, disabled, onChange }) {
  /**
   * True while the user is typing in this field. Only then is the raw value echoed; the moment
   * focus leaves, the stored rule is printed formatted again.
   */
  const [isEditing, setIsEditing] = useState(false);

  const handleChange = (event) => {
    const nextValue = event.target.value;
    if (isValidGradingInput(nextValue) && checkIs3DigitDecimalSigned(nextValue)) {
      onChange(nextValue, rowIndex, sizeIndex);
    }
  };

  const restrictKeys = (event) => {
    if (!GRADING_INPUT_PATTERN.test(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <input
      type="text"
      className="form-control grading-input text-bordered w-100 text-center"
      value={isEditing ? rawGradingValue(value) : trimGradingDecimals(value)}
      onKeyPress={restrictKeys}
      onFocus={() => setIsEditing(true)}
      onBlur={() => setIsEditing(false)}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export default memo(GradingCell);
